import dayjs from 'dayjs';
import { Button, Input, Space } from 'antd';
import { DeleteOutlined, SearchOutlined, PrinterOutlined } from '@ant-design/icons';

// ===== FONCTIONS DE RENDU =====
const renderRemittanceDate = (text, record) => {
  return dayjs(record.remittance_at).format("DD/MM/YYYY");
};

const renderTotalAmount = (text, record) => {
  const total = record.payments?.reduce((sum, payment) => {
    return sum + (parseFloat(payment.montant) || 0);
  }, 0) || 0;

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(total);
};

const renderActions = (handlePrint) => (text, record) => (
  <Space size="small">
    <Button
      type="default"
      size="middle"
      icon={<PrinterOutlined />}
      onClick={() => handlePrint(record)}
      title="Imprimer"
    />
  </Space>
);

// ===== FONCTIONS DE TRI =====
const sortByRemittanceDate = (a, b) => {
  return new Date(a.remittance_at) - new Date(b.remittance_at);
};

// ===== FONCTIONS DE CALCUL =====
const calculateTotal = (record) => {
  return record.payments?.reduce((sum, payment) => {
    return sum + (parseFloat(payment.montant) || 0);
  }, 0) || 0;
};

// ===== COMPOSANTS DE FILTRE =====
const createAmountFilterDropdown = () => {
  return ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
      <Space.Compact style={{ display: 'flex', width: '100%' }}>
        <Input
          placeholder="Rechercher un montant"
          value={selectedKeys[0]}
          size="middle"
          autoFocus
          onChange={(e) => {
            const value = e.target.value;
            setSelectedKeys(value ? [value] : []);
            // Recherche automatique lors de la saisie
            setTimeout(() => confirm({ closeDropdown: false }), 100);
          }}
          onPressEnter={() => confirm()}
          style={{ flex: 1, width: 100 }}
        />
        <Button
          type="default"
          onClick={() => {
            clearFilters();
            confirm();
          }}
          size="middle"
          icon={<DeleteOutlined />}
          title="Effacer le filtre"
        />
      </Space.Compact>
    </div>
  );
};

const createAmountFilterIcon = () => {
  return (filtered) => (
    <SearchOutlined style={{ color: filtered ? '#f4c181' : undefined }} />
  );
};

const createAmountFilter = () => {
  return (value, record) => {
    const total = calculateTotal(record);

    // Si pas de valeur de recherche, afficher tous les résultats
    if (!value || value.trim() === '') return true;

    // Convertir la valeur de recherche en nombre pour la comparaison
    const searchValue = parseFloat(value.replace(',', '.'));
    if (isNaN(searchValue)) {
      // Si ce n'est pas un nombre, rechercher dans la représentation formatée
      const formattedTotal = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(total);
      return formattedTotal.toLowerCase().includes(value.toLowerCase());
    }

    // Filtrer par correspondance approximative pour les nombres
    return Math.abs(total - searchValue) < 0.01;
  };
};

// ===== CONFIGURATIONS DE COLONNES =====
const createNumberColumn = () => ({
  title: "Numéro",
  dataIndex: "number",
  key: "number",
});

const createTitleColumn = () => ({
  title: "Libelle",
  dataIndex: "title",
  key: "title",
});

const createRemittanceDateColumn = () => ({
  title: "Remise du",
  key: "remittance_at",
  dataIndex: "remittance_at",
  align: "center",
  width: 150,
  sorter: sortByRemittanceDate,
  render: renderRemittanceDate,
});

const createTotalAmountColumn = () => ({
  title: "Total",
  key: "totalAmount",
  align: "right",
  width: 150,
  render: renderTotalAmount,
  filterDropdown: createAmountFilterDropdown(),
  filterIcon: createAmountFilterIcon(),
  onFilter: createAmountFilter(),
});

const createActionsColumn = (handlePrint) => ({
  title: "",
  key: "actions",
  align: "center",
  width: 70,
  render: renderActions(handlePrint),
});

// ===== FONCTION PRINCIPALE =====
export const createTableColumns = (handlePrint) => [
  createNumberColumn(),
  createTitleColumn(),
  createRemittanceDateColumn(),
  createTotalAmountColumn(),
  createActionsColumn(handlePrint),
];